import { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Phone, Mail, MapPin, Calendar, Trash2, Eye, X } from 'lucide-react';
import { useAuth, authHeaders } from '../contexts/AuthContext';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-amber-100 text-amber-800',
  quoted: 'bg-purple-100 text-purple-800',
  booked: 'bg-emerald-100 text-emerald-800',
  closed: 'bg-gray-200 text-gray-700',
};

const STATUS_OPTIONS = ['new', 'contacted', 'quoted', 'booked', 'closed'];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState({ total: 0, new: 0, booked: 0, today: 0 });
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [loadingLeads, setLoadingLeads] = useState(true);

  const fetchData = async () => {
    setLoadingLeads(true);
    try {
      const q = filter ? `?status_filter=${filter}` : '';
      const [l, s] = await Promise.all([
        axios.get(`${API}/leads${q}`, { headers: authHeaders() }),
        axios.get(`${API}/leads-stats/summary`, { headers: authHeaders() }),
      ]);
      setLeads(l.data); setSummary(s.data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    } finally { setLoadingLeads(false); }
  };

  useEffect(() => { if (user) fetchData(); }, [user, filter]);

  if (loading) return <div className="min-h-[50vh] grid place-items-center font-manrope text-ink/60">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/leads/${id}`, { status }, { headers: authHeaders() });
    fetchData();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    await axios.delete(`${API}/leads/${id}`, { headers: authHeaders() });
    setSelected(null); fetchData();
  };

  return (
    <>
      <SEO title="Leads Dashboard | Laraé Logistics Admin" description="Internal." />
      <div className="bg-cream min-h-[calc(100vh-160px)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-10">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
            <div>
              <h1 className="font-outfit text-3xl font-medium text-ink">Leads Dashboard</h1>
              <p className="font-manrope text-sm text-ink/60 mt-1">Signed in as {user.email}</p>
            </div>
            <div className="flex gap-2">
              <Link to="/admin/subscribers" data-testid="admin-subscribers-link" className="text-sm font-manrope text-ink/70 hover:text-forest px-4 py-2 rounded-full border border-sand">Subscribers</Link>
              <Link to="/" className="text-sm font-manrope text-ink/70 hover:text-forest px-4 py-2 rounded-full border border-sand">View site</Link>
              <button data-testid="admin-logout-btn" onClick={logout} className="text-sm font-manrope text-white bg-forest px-4 py-2 rounded-full inline-flex items-center gap-2"><LogOut size={14} /> Logout</button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Stat label="Total leads" value={summary.total} />
            <Stat label="New" value={summary.new} accent />
            <Stat label="Today" value={summary.today} />
            <Stat label="Booked" value={summary.booked} />
          </div>

          {/* Filter */}
          <div className="flex flex-wrap gap-2 mb-5">
            <button onClick={() => setFilter('')} className={`px-4 py-1.5 text-xs font-manrope rounded-full border ${filter === '' ? 'bg-forest text-white border-forest' : 'bg-white border-sand text-ink/70'}`}>All</button>
            {STATUS_OPTIONS.map(s => (
              <button key={s} onClick={() => setFilter(s)} className={`px-4 py-1.5 text-xs font-manrope rounded-full border capitalize ${filter === s ? 'bg-forest text-white border-forest' : 'bg-white border-sand text-ink/70'}`}>{s}</button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl border border-sand overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-manrope">
                <thead className="bg-sand/60 text-ink">
                  <tr>
                    <th className="text-left px-5 py-3 font-medium">Name</th>
                    <th className="text-left px-5 py-3 font-medium">Contact</th>
                    <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Route</th>
                    <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Date</th>
                    <th className="text-left px-5 py-3 font-medium">Status</th>
                    <th className="text-right px-5 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingLeads ? (
                    <tr><td colSpan={6} className="text-center py-10 text-ink/50">Loading…</td></tr>
                  ) : leads.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-10 text-ink/50">No leads yet.</td></tr>
                  ) : leads.map(l => (
                    <tr key={l.id} className="border-t border-sand hover:bg-sand/30">
                      <td className="px-5 py-4">
                        <div className="font-medium text-ink">{l.full_name}</div>
                        <div className="text-xs text-ink/50">{new Date(l.created_at).toLocaleString('en-GB')}</div>
                      </td>
                      <td className="px-5 py-4 text-ink/70">
                        <div>{l.phone}</div>
                        <div className="text-xs text-ink/50">{l.email}</div>
                      </td>
                      <td className="px-5 py-4 text-ink/70 hidden md:table-cell max-w-[200px] truncate">
                        {l.collection_address?.split(',')[0]} → {l.delivery_address?.split(',')[0]}
                      </td>
                      <td className="px-5 py-4 text-ink/70 hidden md:table-cell text-xs">{l.move_date || '—'}</td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize ${STATUS_COLORS[l.status] || ''}`}>{l.status}</span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button onClick={() => setSelected(l)} className="p-1.5 rounded-full hover:bg-sand text-ink/70" aria-label="View"><Eye size={16} /></button>
                        <button onClick={() => deleteLead(l.id)} className="p-1.5 rounded-full hover:bg-red-50 text-red-600 ml-1" aria-label="Delete"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {selected && <LeadModal lead={selected} onClose={() => setSelected(null)} onStatusChange={updateStatus} onDelete={deleteLead} />}
      </div>
    </>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-2xl p-5 ${accent ? 'bg-forest text-white' : 'bg-white border border-sand'}`}>
      <div className={`font-outfit text-3xl font-semibold ${accent ? 'text-white' : 'text-ink'}`}>{value}</div>
      <div className={`font-manrope text-xs mt-1 ${accent ? 'text-white/80' : 'text-ink/60'}`}>{label}</div>
    </div>
  );
}

function LeadModal({ lead, onClose, onStatusChange, onDelete }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-sand p-5 flex items-center justify-between">
          <div>
            <h3 className="font-outfit text-xl font-semibold text-ink">{lead.full_name}</h3>
            <p className="text-xs text-ink/50 font-manrope">{new Date(lead.created_at).toLocaleString('en-GB')}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-sand rounded-full"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <a href={`tel:${lead.phone}`} className="bg-forest text-white text-sm rounded-full px-4 py-2.5 inline-flex items-center justify-center gap-2"><Phone size={14} /> Call</a>
            <a href={`mailto:${lead.email}`} className="bg-sand text-ink text-sm rounded-full px-4 py-2.5 inline-flex items-center justify-center gap-2"><Mail size={14} /> Email</a>
          </div>

          <div>
            <label className="text-xs font-medium text-ink/60 font-manrope uppercase tracking-wider mb-2 block">Status</label>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map(s => (
                <button key={s} onClick={() => onStatusChange(lead.id, s)} className={`px-3 py-1.5 text-xs rounded-full capitalize ${lead.status === s ? 'bg-forest text-white' : 'bg-sand text-ink/70'}`}>{s}</button>
              ))}
            </div>
          </div>

          <Row icon={Phone} label="Phone" value={lead.phone} />
          <Row icon={Mail} label="Email" value={lead.email} />
          <Row icon={MapPin} label="From" value={lead.collection_address} />
          <Row icon={MapPin} label="To" value={lead.delivery_address} />
          <Row icon={Calendar} label="Move date" value={`${lead.move_date || '—'} ${lead.preferred_time || ''}`} />
          <Row label="Property" value={`${lead.property_type || '—'} · ${lead.bedrooms || '—'}`} />
          <Row label="Lift / Stairs / Parking" value={`${lead.lift_available || '—'} / ${lead.stairs || '—'} / ${lead.parking_available || '—'}`} />
          <Row label="Services" value={(lead.services_required || []).join(', ') || '—'} />
          {lead.inventory_notes && <Row label="Notes" value={lead.inventory_notes} />}
          {lead.photos?.length > 0 && (
            <div>
              <label className="text-xs font-medium text-ink/60 font-manrope uppercase tracking-wider mb-2 block">Photos ({lead.photos.length})</label>
              <div className="grid grid-cols-3 gap-2">
                {lead.photos.map((p, i) => <img key={i} src={p} alt="" className="rounded-lg border border-sand object-cover aspect-square" />)}
              </div>
            </div>
          )}
          <div className="pt-3 border-t border-sand">
            <button onClick={() => onDelete(lead.id)} className="text-red-600 text-sm font-medium hover:underline inline-flex items-center gap-2"><Trash2 size={14} /> Delete lead</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && <Icon size={16} className="text-ink/40 mt-0.5 shrink-0" />}
      <div className="flex-1">
        <div className="text-xs font-medium text-ink/50 font-manrope">{label}</div>
        <div className="text-sm text-ink font-manrope break-words">{value || '—'}</div>
      </div>
    </div>
  );
}
